import { useQuery } from "@tanstack/react-query";
import React from "react";
import { useForm, useWatch } from "react-hook-form";
import { useLocation, useNavigate, useParams } from "react-router";

import type { NotificationSettingsDto } from "@/api/types";
import { Breadcrumbs } from "@/components/Breadcrumbs/Breadcrumbs";
import { ErrorPage } from "@/components/Error/ErrorPage";
import { useFlashToast } from "@/components/FlashToast/FlashToast";
import { Form } from "@/components/Form/Form";
import { FormSelect } from "@/components/Form/FormSelect";
import { FormSwitch } from "@/components/Form/FormSwitch";
import { AlertTriangleIcon } from "@/components/Icons/AlertTriangleIcon";
import { Bell01Icon } from "@/components/Icons/Bell01Icon";
import { Mail01Icon } from "@/components/Icons/Mail01Icon";
import { PieChart01Icon } from "@/components/Icons/PieChart01Icon";
import { Tool02Icon } from "@/components/Icons/Tool02Icon";
import { NotificationSettingsTable } from "@/components/NotificationSettingsTable/NotificationSettingsTable";
import { DocumentPage } from "@/components/Page/DocumentPage";
import { Button } from "@/design-system/Button";
import { FullSizeLoader } from "@/design-system/FullSizeLoader";
import { SectionCard } from "@/design-system/SectionCard";
import { Stack } from "@/design-system/Stack";
import { useLinkedProjects } from "@/hooks/Network/useLinkedProjects";
import { useSessionUser } from "@/hooks/Network/useSessionUser";
import { useBool } from "@/hooks/useBool";
import { useSlug } from "@/hooks/useSlug";
import { EditUserNotificationSettingsConfirmationModal } from "@/modules/admin-notifications/pages/settings/components/EditUserNotificationSettingsConfirmationModal";
import { userMutations } from "@/queries/users/mutations";
import { useUserQueries } from "@/queries/users/queryOptions";
import { routes } from "@/routes";
import { useTranslation } from "@/translations";

interface EditAdminNotificationSettingsFormValues {
  settings: NotificationSettingsDto;
}

export function NotificationSettingsPage(): React.ReactNode {
  const [isEditConfirmationModalOpened, editConfirmationModalHandler] = useBool(false);

  const slug = useSlug();
  const { t } = useTranslation();
  const { userId: userIdParam } = useParams<{ userId: string }>();
  const sessionUser = useSessionUser();
  const location = useLocation();
  const userId = userIdParam ?? sessionUser.id;
  const navigate = useNavigate();
  const showFlashToast = useFlashToast();

  const isSelfEdit = userId === sessionUser.id;
  const userQueries = useUserQueries();
  const { data: user } = useQuery({
    ...userQueries.details({ userId: userId }),
    enabled: !!userId,
  });
  const {
    data: notificationSettings,
    isPending: isPendingNotificationSettings,
    error: notificationSettingsError,
  } = useQuery(userQueries.notificationSettings({ userId }));
  // Non-blocking query
  const { data: userLinkedProjects = [] } = useQuery({
    ...userQueries.listLinkedProjects({ userId }),
    enabled: !isSelfEdit && !!user && user.isAdmin,
  });
  const { data: selfLinkedProjects = [] } = useLinkedProjects({
    isEnabled: isSelfEdit && !!user && user.isAdmin,
  });
  const updateSettings = userMutations.useUpdateNotificationSettings();

  const formMethods = useForm<EditAdminNotificationSettingsFormValues>({
    defaultValues: {
      settings: notificationSettings,
    },
    values: {
      settings: notificationSettings || {
        generalMessageNew: { emailEnabled: false, dashboardEnabled: false, appEnabled: false },
        generalMessageCommentNew: { emailEnabled: false, dashboardEnabled: false, appEnabled: false },
        chatNew: { emailEnabled: false, dashboardEnabled: false, appEnabled: false },
        sendCommunitySummaryEmailInterval: "never",
        sendMonthlyReportingEmail: false,
        ticketAssignToMe: { emailEnabled: false, dashboardEnabled: false, appEnabled: false },
        ticketAssignedCommentNew: { emailEnabled: false, dashboardEnabled: false, appEnabled: false },
        ticketUnassignedUpdates: [],
        cancelledReservations: { appEnabled: false },
        eventChange: {
          appEnabled: false,
        },
        eventNew: {
          appEnabled: false,
        },
        eventReminder: {
          appEnabled: false,
        },
        groups: [],
        bringmeUpdates: { appEnabled: false },
        myPupUpdates: { appEnabled: false },
        myTicketNewComment: {
          appEnabled: false,
        },
        myTicketStatusChange: {
          appEnabled: false,
        },
        newGeneralAnnouncement: {
          appEnabled: false,
        },
        newGeneralAnnouncementComments: {
          appEnabled: false,
        },
        reservationReminders: {
          appEnabled: false,
        },
        surveyNew: {
          appEnabled: false,
        },
        newGeneralMessageInProjectConnection: {
          appEnabled: false,
        },
      },
    },
  });
  const summaryInterval = useWatch({
    control: formMethods.control,
    name: "settings.sendCommunitySummaryEmailInterval",
  });

  const isLoading = isPendingNotificationSettings;
  if (isLoading) {
    return <FullSizeLoader withPadding />;
  }

  const error = notificationSettingsError;
  if (error) {
    return <ErrorPage error={error} />;
  }

  function handleSubmit(formValues: EditAdminNotificationSettingsFormValues) {
    const canBulkEdit = sessionUser.isAdmin && bulkEditProjects.length > 1;

    if (canBulkEdit) {
      editConfirmationModalHandler.setTrue();
    } else {
      submit(formValues, false);
    }
  }

  function submit(payload: EditAdminNotificationSettingsFormValues, applyToAllProjects: boolean) {
    updateSettings.mutate(
      {
        userId,
        data: {
          ...payload.settings,
          applyToAllProjects,
        },
      },
      {
        onSuccess: () => {
          showFlashToast({ variant: "success", message: t("page.notification-settings.notifications.success") });

          editConfirmationModalHandler.setFalse();
          if (location.state?.fromPage) {
            void navigate(location.state.fromPage);
          } else if (userId !== sessionUser.id) {
            void navigate(routes.users.details({ slug, id: userId }));
          } else {
            void navigate(routes.adminNotifications.list({ slug }));
          }
        },
        onError: () => {
          showFlashToast({ variant: "alert", message: t("page.notification-settings.notifications.failed") });
        },
      },
    );
  }

  const isSummaryDisabled = summaryInterval === "never";

  const bulkEditProjects = isSelfEdit
    ? selfLinkedProjects
    : userLinkedProjects.filter((project) => project.access.canEditNotificationSettings);

  return (
    <>
      <DocumentPage
        containerBoundary="inherit"
        tabTitle={t("page.notification-settings.title")}
        contentSize="constrained"
        breadcrumbs={
          <Breadcrumbs
            pages={[
              {
                name: t("page.admin-notifications.title"),
                to: routes.adminNotifications.list({
                  slug,
                }),
              },
              {
                name: t("page.notification-settings.title"),
              },
            ]}
          />
        }
      >
        <Form.Root<EditAdminNotificationSettingsFormValues> formMethods={formMethods} onSubmit={handleSubmit}>
          <Stack gap="lg">
            <Stack gap="md">
              <h2 className="text-heading-2 text-text-brand-secondary">
                {t("page.notification-settings.group.email")}
              </h2>
              <SectionCard
                title={t("page.notification-settings.project-reporting.title")}
                description={t("page.notification-settings.project-reporting.description")}
                icon={<PieChart01Icon />}
              >
                <NotificationSettingsTable
                  items={[
                    {
                      label: t("page.notification-settings.project-reporting.monthly-project-report"),
                      renderOption: () => (
                        <FormSwitch
                          id="send-monthly-reporting-email"
                          name="settings.sendMonthlyReportingEmail"
                          size="sm"
                        />
                      ),
                    },
                  ]}
                />
              </SectionCard>

              <SectionCard
                title={t("page.notification-settings.community-summary-email.title")}
                description={t("page.notification-settings.community-summary-email.description")}
                icon={<Mail01Icon />}
              >
                <NotificationSettingsTable
                  items={[
                    {
                      label: t("page.notification-settings.community-summary-email.frequency.label"),
                      renderOption: () => (
                        <div className="w-control-narrow">
                          <FormSelect<
                            EditAdminNotificationSettingsFormValues,
                            NotificationSettingsDto["sendCommunitySummaryEmailInterval"]
                          >
                            id="send-community-summary-email-interval"
                            name="settings.sendCommunitySummaryEmailInterval"
                            items={["never", "everyHour", "everyThreeHours", "everyDay", "everyWeek", "everyTwoWeeks"]}
                            keySelector={(x) => x}
                            renderOption={(x) => {
                              switch (x) {
                                case "everyDay":
                                  return t("page.notification-settings.form.email.timed-value.every-day");
                                case "everyHour":
                                  return t("page.notification-settings.form.email.timed-value.every-hour");
                                case "everyThreeHours":
                                  return t("page.notification-settings.form.email.timed-value.every-three-hours");
                                case "everyWeek":
                                  return t("page.notification-settings.form.email.timed-value.every-week");
                                case "everyTwoWeeks":
                                  return t("page.notification-settings.form.email.timed-value.every-two-weeks");
                                case "never":
                                  return t("page.notification-settings.form.email.timed-value.never");
                                default:
                                  return x;
                              }
                            }}
                          />
                        </div>
                      ),
                    },
                    {
                      label: t("page.notification-settings.community-summary-email.new-post"),
                      renderOption: () => (
                        <FormSwitch
                          id="general-message-new-email"
                          name="settings.generalMessageNew.emailEnabled"
                          size="sm"
                          disabled={isSummaryDisabled}
                        />
                      ),
                    },
                    {
                      label: t("page.notification-settings.community-summary-email.new-comment"),
                      renderOption: () => (
                        <FormSwitch
                          id="general-message-comment-new-email"
                          name="settings.generalMessageCommentNew.emailEnabled"
                          size="sm"
                          disabled={isSummaryDisabled}
                        />
                      ),
                    },
                    {
                      label: t("page.notification-settings.community-summary-email.new-chat"),
                      renderOption: () => (
                        <FormSwitch
                          id="chat-new-email"
                          name="settings.chatNew.emailEnabled"
                          size="sm"
                          disabled={isSummaryDisabled}
                        />
                      ),
                    },
                  ]}
                />
              </SectionCard>

              <SectionCard
                title={t("page.notification-settings.ticket-emails.title")}
                description={t("page.notification-settings.ticket-emails.description")}
                icon={<Tool02Icon />}
              >
                <NotificationSettingsTable
                  items={[
                    {
                      label: t("page.notification-settings.ticket-emails.new-ticket-assigned-to-me"),
                      renderOption: () => (
                        <FormSwitch
                          id="ticket-assign-to-me-email"
                          name="settings.ticketAssignToMe.emailEnabled"
                          size="sm"
                        />
                      ),
                    },
                    {
                      label: t("page.notification-settings.ticket-emails.new-comment-on-ticket-assigned-to-me"),
                      renderOption: () => (
                        <FormSwitch
                          id="ticket-assigned-comment-new-email"
                          name="settings.ticketAssignedCommentNew.emailEnabled"
                          size="sm"
                        />
                      ),
                    },
                  ]}
                />
              </SectionCard>
            </Stack>

            <Stack gap="md">
              <h2 className="text-heading-2 text-text-brand-secondary">
                {t("page.notification-settings.group.dashboard")}
              </h2>
              <SectionCard
                title={t("page.notification-settings.dashboard-notifications.title")}
                description={t("page.notification-settings.dashboard-notifications.description")}
                icon={<Bell01Icon />}
              >
                <NotificationSettingsTable
                  items={[
                    {
                      label: t("page.notification-settings.dashboard-notifications.new-post"),
                      renderOption: () => (
                        <FormSwitch
                          id="general-message-new-dashboard"
                          name="settings.generalMessageNew.dashboardEnabled"
                          size="sm"
                        />
                      ),
                    },
                    {
                      label: t("page.notification-settings.dashboard-notifications.new-comment"),
                      renderOption: () => (
                        <FormSwitch
                          id="general-message-comment-new-dashboard"
                          name="settings.generalMessageCommentNew.dashboardEnabled"
                          size="sm"
                        />
                      ),
                    },
                    {
                      label: t("page.notification-settings.dashboard-notifications.new-chat"),
                      renderOption: () => (
                        <FormSwitch id="chat-new-dashboard" name="settings.chatNew.dashboardEnabled" size="sm" />
                      ),
                    },
                    {
                      label: t("page.notification-settings.dashboard-notifications.new-ticket-assigned-to-me"),
                      renderOption: () => (
                        <FormSwitch
                          id="ticket-assign-to-me-dashboard"
                          name="settings.ticketAssignToMe.dashboardEnabled"
                          size="sm"
                        />
                      ),
                    },
                    {
                      label: t("page.notification-settings.dashboard-notifications.new-ticket-comment"),
                      renderOption: () => (
                        <FormSwitch
                          id="ticket-assigned-comment-new-dashboard"
                          name="settings.ticketAssignedCommentNew.dashboardEnabled"
                          size="sm"
                        />
                      ),
                    },
                  ]}
                />
              </SectionCard>

              <SectionCard
                title={t("page.notification-settings.alerts.title")}
                description={t("page.notification-settings.alerts.description")}
                icon={<AlertTriangleIcon />}
              >
                <NotificationSettingsTable
                  items={[
                    {
                      label: t("page.notification-settings.alerts.manage"),
                      href: routes.alerts.settings(),
                    },
                  ]}
                />
              </SectionCard>
            </Stack>

            <Form.Footer
              renderPrimaryAction={() => (
                <Button type="submit" isLoading={updateSettings.isPending}>
                  {t("common.action.save")}
                </Button>
              )}
            />
          </Stack>
        </Form.Root>
      </DocumentPage>
      {bulkEditProjects.length > 1 && (
        <EditUserNotificationSettingsConfirmationModal
          isOpened={isEditConfirmationModalOpened}
          onOpenChange={editConfirmationModalHandler.set}
          projects={bulkEditProjects}
          isSubmitting={updateSettings.isPending}
          onConfirm={(applyToAllProjects: boolean) => submit(formMethods.getValues(), applyToAllProjects)}
        />
      )}
    </>
  );
}
